import {
  Stack,
  Typography,
  Box,
  FormControl,
  MenuItem,
  TextField,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";
import { VoucherService } from "../../../../api/voucher.service";
import { Vouchers } from "../../../../@types/voucher.type";
import { Addon } from "../../../../@types/addon.type";
import { AddonService } from "../../../../api/addon.service";
import { RHFSelect, RHFTextField } from "../../../../components/hook-form";
import {
  Controller,
  useController,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import PickupDateTime from "./PickupDateTime";

interface AddonAndVoucherProps {
  control: any;
  disabled?: boolean;
  date: Date | null;
  setDate: (date: Date | null) => void;
  time: string | null;
  setTime: (time: string | null) => void;
  addons: Addon[];
  vouchers: Vouchers[];
  setAddons: React.Dispatch<React.SetStateAction<Addon[]>>;
  setVouchers: React.Dispatch<React.SetStateAction<Vouchers[]>>;
  framesAndLensesPrice?: number;
  initialManualDiscount?: number;
}

export default function AddonAndVoucher({
  control,
  disabled,
  date,
  setDate,
  time,
  setTime,
  addons,
  vouchers,
  setAddons,
  setVouchers,
  framesAndLensesPrice = 0,
  initialManualDiscount,
}: AddonAndVoucherProps) {
  const [adjustedPrice, setAdjustedPrice] = useState<number>(0);
  const [dynamicDiscount, setDynamicDiscount] = useState<number>(0);
  const [discountError, setDiscountError] = useState<string>("");

  const {
    fields: addonFields,
    append: appendAddon,
    remove: removeAddon,
  } = useFieldArray({
    control,
    name: "addons",
  });

  const {
    fields: voucherFields,
    append: appendVoucher,
    remove: removeVoucher,
  } = useFieldArray({
    control,
    name: "vouchers",
  });

  const watchedAddons = useWatch({ control, name: "addons" });
  const watchedVouchers = useWatch({ control, name: "vouchers" });

  const { field: adjustedPriceField } = useController({
    name: "adjustedPrice",
    control,
  });

  const { field: totalPriceField } = useController({
    name: "totalPrice",
    control,
  });

  const { field: discountField } = useController({
    name: "discount",
    control,
  });

  const getAllVouchers = async () => {
    try {
      const voucherService = new VoucherService();
      const response = await voucherService.getAllVouchers();
      setVouchers(response.data);
    } catch (error) {
      console.error("Error fetching vouchers:", error);
    }
  };

  const getAllAddon = async () => {
    try {
      const addonService = new AddonService();
      const response = await addonService.getAllAddons();
      setAddons(response.data);
    } catch (error) {
      console.error("Error fetching addons:", error);
    }
  };

  const calculateTotalPrice = () => {
    // Start with Frames & Lenses price
    let baseTotal = framesAndLensesPrice;

    // Add addon prices (no discounts on addons anymore)
    watchedAddons?.forEach((addon: any, index: number) => {
      const selectedAddon = addons.find(
        (a) => a.id === watchedAddons?.[index]?.addon,
      );
      if (selectedAddon) {
        baseTotal += selectedAddon.price;
      }
    });

    // Subtract voucher discounts (vouchers now only provide discounts)
    let voucherDiscount = 0;
    watchedVouchers?.forEach((voucher: any, index: number) => {
      const selectedVoucher = vouchers.find(
        (v) => v.id === watchedVouchers?.[index]?.voucher,
      );
      if (selectedVoucher) {
        // Voucher discount is the voucher's price value
        voucherDiscount += selectedVoucher.price;
      }
    });

    // Subtract additional manual discount
    const totalDiscount = voucherDiscount + dynamicDiscount;

    let total = baseTotal - totalDiscount;

    // Ensure total doesn't go below 0
    total = Math.max(0, total);

    setAdjustedPrice(total);
    totalPriceField.onChange(baseTotal);
    discountField.onChange(totalDiscount);
  };

  const baseTotal = (() => {
    let total = framesAndLensesPrice;
    watchedAddons?.forEach((_: any, index: number) => {
      const selectedAddon = addons.find(
        (a) => a.id === watchedAddons?.[index]?.addon,
      );
      if (selectedAddon) total += selectedAddon.price;
    });
    return total;
  })();

  const voucherDiscount = (() => {
    let discount = 0;
    watchedVouchers?.forEach((_: any, index: number) => {
      const selectedVoucher = vouchers.find(
        (v) => v.id === watchedVouchers?.[index]?.voucher,
      );
      if (selectedVoucher) discount += selectedVoucher.price;
    });
    return discount;
  })();

  // Max the user can manually discount = base total minus voucher discounts already applied
  const maxManualDiscount = Math.max(0, baseTotal - voucherDiscount);

  useEffect(() => {
    getAllVouchers();
    getAllAddon();
  }, []);

  useEffect(() => {
    if (initialManualDiscount !== undefined) {
      setDynamicDiscount(initialManualDiscount);
    }
  }, [initialManualDiscount]);

  useEffect(
    () => calculateTotalPrice(),
    [
      watchedAddons,
      watchedVouchers,
      addons,
      vouchers,
      framesAndLensesPrice,
      dynamicDiscount,
    ],
  );

  useEffect(() => {
    adjustedPriceField.onChange(adjustedPrice);
  }, [adjustedPrice]);

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={2} p={1}>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{ display: "flex", alignItems: "end", width: "100%", mb: 2 }}>
            <Typography variant="h6" sx={{ mr: 1 }}>
              Tints/Coatings etc
            </Typography>
            <Box sx={{ flexGrow: 1, borderBottom: "1px solid #ccc", mb: 0 }} />
            <Button
              variant="outlined"
              sx={{ ml: 1 }}
              onClick={() => appendAddon({ addon: "", discount: 0 })}
              disabled={disabled}>
              Add
            </Button>
          </Box>
          <Stack direction="column" spacing={2} mt={1}>
            {addonFields.map((item, index) => {
              const selectedAddon = addons.find(
                (addon) => addon.id === (watchedAddons?.[index]?.addon || ""),
              );
              const addonPrice = selectedAddon ? selectedAddon.price : 0;
              return (
                <Box
                  key={item.id}
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <FormControl
                    variant="standard"
                    sx={{ my: 1, mr: 1, flex: 1 }}>
                    <Controller
                      name={`addons.${index}.addon`}
                      control={control}
                      render={({ field }) => (
                        <RHFSelect
                          {...field}
                          label="Addon"
                          onChange={(event) => field.onChange(event)}
                          disabled={disabled}>
                          <MenuItem value="">
                            <em>Select Addon</em>
                          </MenuItem>
                          {addons.map((addonItem) => (
                            <MenuItem key={addonItem.id} value={addonItem.id}>
                              {addonItem.name}
                            </MenuItem>
                          ))}
                        </RHFSelect>
                      )}
                    />
                  </FormControl>
                  <TextField
                    variant="standard"
                    label="Price"
                    value={addonPrice}
                    disabled
                    sx={{ width: "100px", mx: 2 }}
                    InputProps={{
                      sx: { textAlign: "center" },
                    }}
                    inputProps={{
                      style: { textAlign: "center" },
                    }}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => removeAddon(index)}
                    disabled={disabled}>
                    Remove
                  </Button>
                </Box>
              );
            })}
          </Stack>
        </Box>

        <Box sx={{ width: "100%" }}>
          <Box
            sx={{ display: "flex", alignItems: "end", width: "100%", mb: 2 }}>
            <Typography variant="h6" sx={{ mr: 1 }}>
              Vouchers
            </Typography>
            <Box sx={{ flexGrow: 1, borderBottom: "1px solid #ccc", mb: 0 }} />
            <Button
              variant="outlined"
              sx={{ ml: 1 }}
              onClick={() => appendVoucher({ voucher: "", discount: 0 })}
              disabled={disabled}>
              Add
            </Button>
          </Box>
          <Stack direction="column" spacing={2} mt={1}>
            {voucherFields.map((item, index) => {
              const selectedVoucher = vouchers.find(
                (voucher) =>
                  voucher.id === (watchedVouchers?.[index]?.voucher || ""),
              );
              const voucherDiscount = selectedVoucher
                ? selectedVoucher.price
                : 0;
              return (
                <Box
                  key={item.id}
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <FormControl
                    variant="standard"
                    sx={{ my: 1, mr: 1, flex: 1 }}>
                    <Controller
                      name={`vouchers.${index}.voucher`}
                      control={control}
                      render={({ field }) => (
                        <RHFSelect
                          {...field}
                          label="Voucher"
                          onChange={(event) => field.onChange(event)}
                          disabled={disabled}>
                          <MenuItem value="">
                            <em>Select Voucher</em>
                          </MenuItem>
                          {vouchers.map((voucherItem) => (
                            <MenuItem
                              key={voucherItem.id}
                              value={voucherItem.id}>
                              {voucherItem.name}
                            </MenuItem>
                          ))}
                        </RHFSelect>
                      )}
                    />
                  </FormControl>
                  <TextField
                    variant="standard"
                    label="Discount"
                    value={voucherDiscount}
                    disabled
                    sx={{ width: "100px", mx: 2 }}
                    InputProps={{
                      sx: { textAlign: "center" },
                    }}
                    inputProps={{
                      style: { textAlign: "center" },
                    }}
                  />

                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => removeVoucher(index)}
                    disabled={disabled}>
                    Remove
                  </Button>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Stack>

      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <PickupDateTime
          date={date}
          setDate={setDate}
          time={time}
          setTime={setTime}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "start",
            width: "100%",
            p: 1,
          }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
              gap: 2,
            }}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                Discount
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}>
                <TextField
                  variant="standard"
                  size="small"
                  type="number"
                  value={dynamicDiscount === 0 ? "" : dynamicDiscount}
                  onChange={(e) => {
                    const val = e.target.value;
                    const num = val === "" ? 0 : Math.max(0, Number(val));
                    setDynamicDiscount(num);
                    if (num > maxManualDiscount) {
                      setDiscountError(
                        `Discount cannot exceed £${maxManualDiscount.toFixed(2)}`,
                      );
                    } else {
                      setDiscountError("");
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      setDynamicDiscount(0);
                      setDiscountError("");
                    }
                  }}
                  error={!!discountError}
                  sx={{ width: 80 }}
                  inputProps={{ style: { textAlign: "center" } }}
                />
                {discountError && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, maxWidth: 180, textAlign: "right" }}>
                    {discountError}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                Total Price
              </Typography>
              <TextField
                variant="standard"
                size="small"
                name="adjustedPrice"
                value={adjustedPrice}
                disabled
                sx={{ width: 80 }}
                inputProps={{ style: { textAlign: "center" } }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
